// Canonical profile types — mirrors the Pydantic models in
// agents/_shared/schemas.py. Add new fields here AND in Pydantic, never
// just one side.
//
// CR-2026-05-09-001: three-concept ontology (Account / Holding / Cashflow).
// Replaced legacy FinancialAsset / Position / Instrument.
// ── Friendly badge labels for the canonical AccountType ────────────────────
export const ACCOUNT_CLASS_BADGE = {
    asset: { label: "Asset", cls: "bg-emerald-500/20 text-emerald-400" },
    liability: { label: "Liability", cls: "bg-rose-500/20 text-rose-400" },
};
export const CANONICAL_ACCOUNT_TYPE_BADGE = {
    // asset deposit
    checking: { label: "Checking", cls: "bg-emerald-500/20 text-emerald-400" },
    savings: { label: "Savings", cls: "bg-emerald-500/20 text-emerald-400" },
    money_market: { label: "Money Market", cls: "bg-emerald-500/20 text-emerald-400" },
    cd: { label: "CD", cls: "bg-emerald-500/20 text-emerald-400" },
    hsa_cash: { label: "HSA (cash)", cls: "bg-emerald-500/20 text-emerald-400" },
    // asset investment
    brokerage: { label: "Brokerage", cls: "bg-blue-500/20 text-blue-400" },
    roth_ira: { label: "Roth IRA", cls: "bg-amber-500/20 text-amber-400" },
    traditional_ira: { label: "Trad. IRA", cls: "bg-amber-500/20 text-amber-400" },
    rollover_ira: { label: "Rollover IRA", cls: "bg-amber-500/20 text-amber-400" },
    sep_ira: { label: "SEP IRA", cls: "bg-amber-500/20 text-amber-400" },
    simple_ira: { label: "SIMPLE IRA", cls: "bg-amber-500/20 text-amber-400" },
    "401k": { label: "401(k)", cls: "bg-amber-500/20 text-amber-400" },
    roth_401k: { label: "Roth 401(k)", cls: "bg-amber-500/20 text-amber-400" },
    "403b": { label: "403(b)", cls: "bg-amber-500/20 text-amber-400" },
    "457b": { label: "457(b)", cls: "bg-amber-500/20 text-amber-400" },
    tsp: { label: "TSP", cls: "bg-amber-500/20 text-amber-400" },
    hsa_invested: { label: "HSA (inv)", cls: "bg-amber-500/20 text-amber-400" },
    "529": { label: "529", cls: "bg-fuchsia-500/20 text-fuchsia-400" },
    utma_ugma: { label: "UTMA/UGMA", cls: "bg-fuchsia-500/20 text-fuchsia-400" },
    pension: { label: "Pension", cls: "bg-amber-500/20 text-amber-400" },
    annuity: { label: "Annuity", cls: "bg-amber-500/20 text-amber-400" },
    // asset tangible
    real_estate: { label: "Real Estate", cls: "bg-slate-500/20 text-slate-400" },
    vehicle: { label: "Vehicle", cls: "bg-gray-500/20 text-gray-400" },
    collectible: { label: "Collectible", cls: "bg-gray-500/20 text-gray-400" },
    business_equity: { label: "Business", cls: "bg-gray-500/20 text-gray-400" },
    private_equity: { label: "Private Equity", cls: "bg-blue-500/20 text-blue-400" },
    crypto_wallet: { label: "Crypto", cls: "bg-purple-500/20 text-purple-400" },
    cash_on_hand: { label: "Cash", cls: "bg-emerald-500/20 text-emerald-400" },
    other_asset: { label: "Other", cls: "bg-gray-500/20 text-gray-400" },
    // liability
    mortgage: { label: "Mortgage", cls: "bg-rose-500/20 text-rose-400" },
    heloc: { label: "HELOC", cls: "bg-rose-500/20 text-rose-400" },
    auto_loan: { label: "Auto Loan", cls: "bg-rose-500/20 text-rose-400" },
    student_loan: { label: "Student Loan", cls: "bg-rose-500/20 text-rose-400" },
    personal_loan: { label: "Personal Loan", cls: "bg-rose-500/20 text-rose-400" },
    business_loan: { label: "Business Loan", cls: "bg-rose-500/20 text-rose-400" },
    margin_loan: { label: "Margin Loan", cls: "bg-rose-500/20 text-rose-400" },
    tax_debt: { label: "Tax Debt", cls: "bg-rose-500/20 text-rose-400" },
    credit_card: { label: "Credit Card", cls: "bg-rose-500/20 text-rose-400" },
    line_of_credit: { label: "Line of Credit", cls: "bg-rose-500/20 text-rose-400" },
    other_liability: { label: "Other (debt)", cls: "bg-rose-500/20 text-rose-400" },
};
// ── Closed lists by class for form pickers ─────────────────────────────────
export const ASSET_TYPES = [
    "checking", "savings", "money_market", "cd", "hsa_cash",
    "brokerage", "roth_ira", "traditional_ira", "rollover_ira",
    "sep_ira", "simple_ira",
    "401k", "roth_401k", "403b", "457b", "tsp",
    "hsa_invested", "529", "utma_ugma",
    "pension", "annuity",
    "real_estate", "vehicle", "collectible",
    "business_equity", "private_equity",
    "crypto_wallet", "cash_on_hand",
    "other_asset",
];
export const LIABILITY_TYPES = [
    "mortgage", "heloc", "auto_loan", "student_loan",
    "personal_loan", "business_loan", "margin_loan", "tax_debt",
    "credit_card", "line_of_credit",
    "other_liability",
];
export const INVESTMENT_TYPES = new Set([
    "brokerage", "roth_ira", "traditional_ira", "rollover_ira",
    "sep_ira", "simple_ira",
    "401k", "roth_401k", "403b", "457b", "tsp",
    "hsa_invested", "529", "utma_ugma",
    "annuity", "private_equity",
]);
export const RETIREMENT_TYPES = new Set([
    "roth_ira", "traditional_ira", "rollover_ira",
    "sep_ira", "simple_ira",
    "401k", "roth_401k", "403b", "457b", "tsp",
    "pension", "annuity",
]);
// Canonical (type → tax_treatment) map. Use to auto-fill the picker.
export const CANONICAL_TAX_TREATMENT = {
    checking: "taxable", savings: "taxable", money_market: "taxable",
    cd: "taxable", brokerage: "taxable", utma_ugma: "taxable",
    cash_on_hand: "taxable",
    traditional_ira: "tax_deferred", rollover_ira: "tax_deferred",
    sep_ira: "tax_deferred", simple_ira: "tax_deferred",
    "401k": "tax_deferred", "403b": "tax_deferred", "457b": "tax_deferred",
    tsp: "tax_deferred", annuity: "tax_deferred", pension: "tax_deferred",
    roth_ira: "tax_free", roth_401k: "tax_free",
    hsa_cash: "tax_free", hsa_invested: "tax_free", "529": "tax_free",
    real_estate: "na", vehicle: "na", collectible: "na",
    business_equity: "na", private_equity: "na",
    crypto_wallet: "na", other_asset: "na",
    mortgage: "na", heloc: "na", auto_loan: "na",
    student_loan: "na", personal_loan: "na", business_loan: "na",
    margin_loan: "na", tax_debt: "na",
    credit_card: "na", line_of_credit: "na", other_liability: "na",
};
// Friendly badge for the LEGACY 10-value enum (kept for the existing UI).
export const ACCOUNT_TYPE_BADGE = {
    retirement_account: { label: "Retirement", cls: "bg-amber-500/20 text-amber-400" },
    brokerage: { label: "Brokerage", cls: "bg-blue-500/20 text-blue-400" },
    savings: { label: "Savings", cls: "bg-emerald-500/20 text-emerald-400" },
    education: { label: "Education", cls: "bg-fuchsia-500/20 text-fuchsia-400" },
    crypto: { label: "Crypto", cls: "bg-purple-500/20 text-purple-400" },
    property: { label: "Property", cls: "bg-slate-500/20 text-slate-400" },
    business: { label: "Business", cls: "bg-gray-500/20 text-gray-400" },
    life_insurance: { label: "Insurance", cls: "bg-cyan-500/20 text-cyan-400" },
    vehicle: { label: "Vehicle", cls: "bg-gray-500/20 text-gray-400" },
    other: { label: "Other", cls: "bg-gray-500/20 text-gray-400" },
};
// Canonical AccountType → legacy 10-value AccountType (for badge / colour).
const _CANONICAL_TO_LEGACY_ASSET = {
    checking: "savings", savings: "savings", money_market: "savings",
    cd: "savings", hsa_cash: "savings", cash_on_hand: "savings",
    brokerage: "brokerage", utma_ugma: "brokerage",
    roth_ira: "retirement_account", traditional_ira: "retirement_account",
    rollover_ira: "retirement_account", sep_ira: "retirement_account",
    simple_ira: "retirement_account",
    "401k": "retirement_account", roth_401k: "retirement_account",
    "403b": "retirement_account", "457b": "retirement_account",
    tsp: "retirement_account", hsa_invested: "retirement_account",
    pension: "retirement_account", annuity: "retirement_account",
    "529": "education",
    real_estate: "property", vehicle: "vehicle", collectible: "other",
    business_equity: "business", private_equity: "business",
    crypto_wallet: "crypto", other_asset: "other",
};
const _CANONICAL_INCOME_KINDS = new Set([
    "earned_income", "unearned_income", "benefit_income",
    "rental_income", "business_income",
]);
const _CANONICAL_EXPENSE_KINDS = new Set([
    "living_expense", "tax_payment", "insurance_premium",
]);
const _ANNUAL_PER_FREQ = {
    weekly: 52, biweekly: 26, semimonthly: 24,
    monthly: 12, quarterly: 4, annual: 1, one_time: 0,
};
/** Adapt a canonical FinancialPosition (`{ schema_version, accounts, cashflows }`)
 *  into the legacy view the existing UI components render. View-only.
 *  When the input doesn't carry `accounts`, returns it unchanged so any
 *  legacy snapshot from before the canonical migration still renders.
 */
export function adaptCanonicalToLegacyView(fp) {
    if (!fp || !Array.isArray(fp.accounts)) {
        return fp ?? {};
    }
    const accounts = (fp.accounts ?? []);
    const cashflows = (fp.cashflows ?? []);
    const financial_assets = [];
    const instruments = [];
    const positions = [];
    let total_assets_value = 0;
    let total_debt = 0;
    let monthly_debt_burden = 0;
    for (const a of accounts) {
        const balance = Number(a.balance ?? 0);
        if (a.class === "asset") {
            const legacyType = _CANONICAL_TO_LEGACY_ASSET[a.type] ?? "other";
            financial_assets.push({
                id: a.id, label: a.label, type: legacyType,
                value: balance, status: "active",
            });
            total_assets_value += balance;
            if (a.holdings) {
                for (const h of a.holdings) {
                    positions.push({
                        symbol: h.symbol ?? h.name ?? "—",
                        fund_name: h.name,
                        value: Number(h.market_value ?? 0),
                        quantity: h.quantity ? Number(h.quantity) : undefined,
                        account_id: a.id,
                        account_type: legacyType,
                        account_label: a.label,
                    });
                }
            }
        }
        else {
            const monthly = Number(a.minimum_payment ?? 0);
            instruments.push({
                id: a.id, label: a.label, type: a.type,
                balance: balance,
                monthly_payment: monthly,
                rate: a.interest_rate ? Number(a.interest_rate) : undefined,
                status: "active",
            });
            total_debt += balance;
            monthly_debt_burden += monthly;
        }
    }
    let annual_gross_income = 0;
    let monthly_expenses = 0;
    for (const cf of cashflows) {
        const mult = _ANNUAL_PER_FREQ[cf.frequency] ?? 0;
        const annual = Number(cf.amount ?? 0) * mult;
        if (_CANONICAL_INCOME_KINDS.has(cf.kind)) {
            annual_gross_income += annual;
        }
        else if (_CANONICAL_EXPENSE_KINDS.has(cf.kind)) {
            monthly_expenses += annual / 12;
        }
    }
    const net_worth = total_assets_value - total_debt;
    const debt_to_income = annual_gross_income > 0 ? total_debt / annual_gross_income : undefined;
    return {
        financial_assets,
        instruments,
        positions,
        annual_gross_income,
        monthly_expenses,
        total_assets_value,
        total_debt,
        monthly_debt_burden,
        net_worth,
        debt_to_income,
    };
}
